import { registerMediaElementForAnalysis } from "./visualizer.js";

export type PlayerEvent = "play" | "pause" | "ended" | "timeupdate" | "loadedmetadata" | "error";

export interface HtmlAudioPlayerOptions {
  autoplay?: boolean;
  preload?: "none" | "metadata" | "auto";
  crossOrigin?: "" | "anonymous" | "use-credentials";
  visualizer?: boolean;
}

/**
 * Thin wrapper around HTMLAudioElement matching audioplayers-style usage in the Flutter app.
 */
export class HtmlAudioPlayer {
  readonly element: HTMLAudioElement;
  private listeners = new Map<PlayerEvent, Set<() => void>>();

  constructor(options: HtmlAudioPlayerOptions = {}) {
    this.element = new Audio();
    this.element.preload = options.preload ?? "auto";
    this.element.setAttribute("playsinline", "");
    if (options.crossOrigin) {
      this.element.crossOrigin = options.crossOrigin;
    }
    if (options.autoplay) this.element.autoplay = true;

    this.element.addEventListener("play", () => this.emit("play"));
    this.element.addEventListener("pause", () => this.emit("pause"));
    this.element.addEventListener("ended", () => this.emit("ended"));
    this.element.addEventListener("timeupdate", () => this.emit("timeupdate"));
    this.element.addEventListener("loadedmetadata", () => this.emit("loadedmetadata"));
    this.element.addEventListener("error", () => this.emit("error"));

    if (options.visualizer) {
      registerMediaElementForAnalysis(this.element);
    }
  }

  get currentTime(): number {
    return this.element.currentTime;
  }

  get duration(): number {
    return Number.isFinite(this.element.duration) ? this.element.duration : 0;
  }

  get paused(): boolean {
    return this.element.paused;
  }

  get volume(): number {
    return this.element.volume;
  }

  set volume(value: number) {
    this.element.volume = Math.min(1, Math.max(0, value));
  }

  on(event: PlayerEvent, handler: () => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  off(event: PlayerEvent, handler: () => void): void {
    this.listeners.get(event)?.delete(handler);
  }

  private emit(event: PlayerEvent): void {
    for (const handler of this.listeners.get(event) ?? []) {
      handler();
    }
  }

  async setSource(url: string): Promise<void> {
    this.element.src = url;
    this.element.load();
  }

  /** Resolves once `loadedmetadata` fires or metadata is already available. */
  waitForLoadedMetadata(): Promise<void> {
    if (this.element.readyState >= HTMLMediaElement.HAVE_METADATA) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const onLoaded = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error("Failed to load audio metadata"));
      };
      const cleanup = () => {
        this.element.removeEventListener("loadedmetadata", onLoaded);
        this.element.removeEventListener("error", onError);
      };
      this.element.addEventListener("loadedmetadata", onLoaded);
      this.element.addEventListener("error", onError);
    });
  }

  async setSrcObject(stream: MediaStream): Promise<void> {
    this.element.srcObject = stream;
  }

  async play(): Promise<void> {
    await this.element.play();
  }

  pause(): void {
    this.element.pause();
  }

  stop(): void {
    this.element.pause();
    this.element.currentTime = 0;
  }

  seek(seconds: number): void {
    this.element.currentTime = seconds;
  }

  dispose(): void {
    this.stop();
    this.element.src = "";
    this.element.srcObject = null;
    this.listeners.clear();
  }
}
